import { RefreshCcw } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Button, ErrorState } from '@helios/ui';
import { useOwnSettings, useUpdateUserSettings } from '@/hooks/useSettings';
import { useSessionScopeFence } from '@/hooks/useSessionScope';
import { useAuth } from '@/features/auth/authContext';
import { useHideableNavGroups } from '@/navigation/useSidebarNavGroups';
import { SidebarPagesSettingsCard } from '@/components/layout/SidebarPagesVisibility';
import { SettingsLoadingState, SettingsMutationBanner, UserPreferencesForm, toUserForm, type UserForm } from './settingsShared';
import { SettingsPageFrame } from './settingsChrome';
import { SettingsDraftActions } from './SettingsDraftActions';
import { landingPageOptions } from './landingPageOptions';
import { changedSettingsFields, useSettingsDraft } from './useSettingsDraft';

const editableKeys: readonly (keyof UserForm)[] = ['name', 'title', 'timezone', 'dateFormat', 'landingPage', 'denseMode', 'criticalOnlyNotifications'];
export function PreferencesSettingsPage() {
  const query = useOwnSettings();
  const update = useUpdateUserSettings();
  const auth = useAuth();
  const groups = useHideableNavGroups();
  const { identity, isCurrent } = useSessionScopeFence();
  const draft = useSettingsDraft(query.data ? { form: toUserForm(query.data), version: query.data.version } : null);
  const [notice, setNotice] = useState<{ scope: string; message: string } | null>(null);
  const landingPages = landingPageOptions(groups, auth.permissions);
  const refresh = () => void query.refetch();
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!draft.form || !draft.baseline || draft.version === null || !draft.dirty) return;
    const origin = identity;
    try {
      const saved = await update.mutateAsync({ expectedVersion: draft.version, ...changedSettingsFields(draft.form, draft.baseline, editableKeys) });
      if (!isCurrent(origin)) return;
      draft.accept({ form: toUserForm(saved), version: saved.version });
      setNotice({ scope: draft.scope, message: 'Preferences saved.' });
    } catch { /* The scoped mutation banner preserves the draft and reports the API error. */ }
  };
  if (query.isLoading) return <SettingsLoadingState label="Loading preferences…" />;
  if (!query.data || query.isError) return <ErrorState title="Preferences unavailable" description={query.error instanceof Error ? query.error.message : 'Your own profile and preferences could not be loaded.'} action={<Button size="xs" variant="outline" onClick={refresh}>Retry</Button>} />;
  return <SettingsPageFrame eyebrow="Workspace" title="Preferences" description="Your profile, display settings, landing page, and personal navigation." actions={<><Button size="xs" variant="outline" asChild><Link to="/auth/password-change" state={{ from: { pathname: '/settings/preferences' } }}>Change password</Link></Button><Button size="xs" variant="outline" onClick={refresh}><RefreshCcw className="h-3.5 w-3.5" aria-hidden="true" />Refresh</Button></>}>
    <SettingsMutationBanner message={notice?.scope === draft.scope ? notice.message : null} error={update.error} />
    <SettingsDraftActions dirty={draft.dirty} changedOnServer={draft.changedOnServer} disabled={update.isPending} onRevert={() => { draft.accept(); update.reset(); setNotice(null); }} />
    <UserPreferencesForm form={draft.form} onChange={draft.change} onSubmit={submit} saving={update.isPending} dirty={draft.dirty} landingPages={landingPages} />
    <SidebarPagesSettingsCard />
  </SettingsPageFrame>;
}
